import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { api } from '../../lib/api';
import { runFullInteractionCheck } from '../../lib/interactionCheck';
import InteractionWarningModal from '../prescriptions/InteractionWarningModal';

const formatRelativeDate = (iso) => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Action labels for Verify mode. Server sends `verifyActions` as an array
// of keys; we look up the label/icon/variant here. Keys must match the
// server's VERIFY_ACTIONS constant.
const VERIFY_ACTION_LABELS = {
  sign_off: { label: 'No errors found — sign off as safe', icon: 'bi-check-circle', variant: 'success' },
  amend_myself: { label: 'Amend prescription, sign and annotate', icon: 'bi-pencil-square', variant: 'info' },
  query_with_prescriber: { label: 'Query with the prescriber', icon: 'bi-telephone', variant: 'dark' },
  provide_advice_to_patient: { label: 'Provide advice to the patient', icon: 'bi-chat-square-text', variant: 'primary' },
  refuse_to_dispense: { label: 'Refuse to dispense', icon: 'bi-x-octagon', variant: 'danger' },
  // Kept for back-compat: any historical sessions still carrying refer_back
  // can still render a label. Server no longer offers it as an option.
  refer_back: { label: 'Refer back to the prescriber', icon: 'bi-arrow-left-circle', variant: 'dark' },
};

const verifyActionLabel = (key) => VERIFY_ACTION_LABELS[key]?.label || key || '—';

// Actions that require the student to write a justification. Mirrors the
// server-side VERIFY_ACTIONS_REQUIRE_RATIONALE set. sign_off doesn't need
// one (the action itself says "no errors found").
const VERIFY_ACTIONS_REQUIRE_RATIONALE = new Set([
  'amend_myself',
  'query_with_prescriber',
  'provide_advice_to_patient',
  'refuse_to_dispense',
  'refer_back',
]);

// Prompt shown above the rationale textarea, tailored to the action so the
// student knows what kind of detail to write.
const RATIONALE_PROMPTS = {
  amend_myself: 'What change are you making? Write the annotation as you would on the script.',
  query_with_prescriber: 'What will you say to the prescriber when you contact them?',
  provide_advice_to_patient: 'What advice will you give the patient? Include any safety-netting.',
  refuse_to_dispense: 'Why are you refusing to dispense? What will you tell the patient?',
  refer_back: 'Why are you referring back? What will you ask the prescriber?',
};

const SEVERITY_BADGE = (severity) => {
  if (severity === 'clinical') return { bg: 'danger' };
  if (severity === 'legal') return { bg: 'warning', text: 'dark' };
  if (severity === 'best_practice') return { bg: 'info' };
  return { bg: 'secondary' };
};

const SEVERITY_LABEL = (severity) => {
  if (severity === 'clinical') return 'clinical';
  if (severity === 'legal') return 'legal';
  if (severity === 'best_practice') return 'best practice';
  return severity;
};

const SIMULATION_PRESCRIBER_ADDRESS = 'Auxtechna Training Hospital - SIMULATION ADDRESS - NOT A REAL CLINICIAN';

const COMMON_DOSE_UNITS = [
  'tablet', 'capsule', 'mL', 'mg', 'mcg', 'g',
  'puff', 'spray', 'drop', 'lozenge', 'patch', 'sachet',
  'unit', 'application', 'suppository', 'pessary',
];

const labelForDifficulty = (key) => ({
  starter: { label: 'Starter', tone: 'success' },
  standard: { label: 'Standard', tone: 'primary' },
  intermediate: { label: 'Intermediate', tone: 'warning' },
  advanced: { label: 'Advanced', tone: 'danger' },
})[key] || { label: 'Standard', tone: 'primary' };

const formatLibraryStrength = (item) => {
  const s = String(item?.strength || '').trim();
  const u = String(item?.unit || '').trim();
  if (!s) return '';
  if (!u) return s;
  return s.toLowerCase().includes(u.toLowerCase()) ? s : `${s} ${u}`;
};

// Parse the numeric strength value out of e.g. "1.25mg", "500", "1.25 mg".
// Slash is NOT a unit char so combo strengths like "100/6 micrograms"
// (Fostair) parse as just "100" + the trailing word - we then ignore the
// unit anyway for non-tablet forms.
const parseStrengthNumeric = (item) => {
  const s = String(item?.strength || '').trim();
  const fallbackUnit = String(item?.unit || '').trim();
  const m = s.match(/^([\d.]+)\s*([a-zA-Zμ]+)?/);
  if (!m) return { amount: '', unit: fallbackUnit };
  return { amount: m[1], unit: (m[2] || fallbackUnit).trim() };
};

// Per-formulation dose unit. For tablets/capsules the natural dose is the
// strength itself ("Bisoprolol 1.25mg → 1.25 mg"); for inhalers/sprays/etc
// it's the discrete unit ("Fostair inhaler → 1 puff"); liquids and
// topicals have no good default and the student fills it in.
const FORM_TO_DOSE_UNIT = {
  inhaler: 'puff',
  spray: 'spray',
  drop: 'drop',
  drops: 'drop',
  lozenge: 'lozenge',
  patch: 'patch',
  sachet: 'sachet',
  pessary: 'pessary',
  suppository: 'suppository',
};

const defaultDoseFromItem = (item) => {
  const form = String(item?.form || '').trim().toLowerCase();
  if (form === 'tablet' || form === 'capsule') {
    const { amount, unit } = parseStrengthNumeric(item);
    if (amount) return { amount, unit };
    return { amount: '1', unit: form };
  }
  const mapped = FORM_TO_DOSE_UNIT[form];
  if (mapped) return { amount: '1', unit: mapped };
  return { amount: '', unit: '' };
};

const blankDrugRow = () => ({
  drug: '',
  formulation: '',
  strength: '',
  route: '',
  doseAmount: '',
  doseUnit: '',
  frequency: '',
  durationDays: '',
  durationDoses: '',
  quantity: '',
  quantityInWords: '',
  instalmentAmount: '',
  instalmentInterval: '',
  isControlledDrug: false,
});

const DrugAutocomplete = ({ value, onChange, onPick, library = [], disabled }) => {
  const [focused, setFocused] = useState(false);
  const [hover, setHover] = useState(false);
  const matches = useMemo(() => {
    const q = String(value || '').trim().toLowerCase();
    if (q.length < 2) return [];
    return library
      .filter((item) => {
        const name = String(item.drugName || '').toLowerCase();
        const aliases = String(item.aliases || '').toLowerCase();
        return name.includes(q) || aliases.includes(q);
      })
      .slice(0, 8);
  }, [value, library]);
  const showDropdown = (focused || hover) && matches.length > 0 && !disabled;
  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Form.Control
        size="sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder="Type 2+ characters to search the drug library"
        disabled={disabled}
      />
      {showDropdown ? (
        <div
          style={{
            position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0,
            backgroundColor: '#fff', border: '1px solid #ced4da', borderRadius: 4,
            maxHeight: 240, overflowY: 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          }}
        >
          {matches.map((item) => (
            <button
              type="button"
              key={item.id}
              onMouseDown={(e) => { e.preventDefault(); onPick(item); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '6px 10px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
              }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f1f3f5'; }}
              onFocus={(e) => { e.currentTarget.style.backgroundColor = '#f1f3f5'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              onBlur={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div className="fw-semibold">
                {item.drugName} {formatLibraryStrength(item)}{item.form ? ` (${item.form})` : ''}
                {item.controlledDrug ? <Badge bg="warning" text="dark" className="ms-2">CD</Badge> : null}
              </div>
              <div className="small text-muted">
                {[item.defaultRoute, item.usualFrequencies, item.defaultIndication].filter(Boolean).join(' | ')}
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const SimulationBanner = () => (
  <Alert variant="warning" className="mb-3 text-center fw-bold" style={{ letterSpacing: '0.5px' }}>
    TRAINING SIMULATION - NOT A VALID PRESCRIPTION
  </Alert>
);

const SimulationFooter = () => (
  <Alert variant="warning" className="mt-3 small text-center mb-0">
    This document is for educational purposes only. It is not a valid prescription and cannot be dispensed by any pharmacy.
  </Alert>
);

const ScenarioBanner = ({ scenario }) => {
  if (!scenario) return null;
  return (
    <Alert variant="primary" className="mb-3">
      <h6 className="text-uppercase small mb-2 fw-bold">Clinical scenario</h6>
      <div style={{ whiteSpace: 'pre-wrap' }}>{scenario}</div>
    </Alert>
  );
};

const PatientHeader = ({ patient }) => (
  <Card className="mb-3">
    <Card.Body className="py-3">
      <h6 className="text-uppercase small text-muted mb-2">Patient details</h6>
      <Row className="g-2 small">
        <Col md={6}><strong>Name:</strong> {patient?.name || '—'}</Col>
        <Col md={3}><strong>DOB:</strong> {patient?.dob || '—'}</Col>
        <Col md={3}><strong>Allergies:</strong> {patient?.allergies || 'NKDA'}</Col>
        <Col md={6}><strong>NHS:</strong> {patient?.trainingNhsNumber || 'TRAINING-XXXXXXXXXX'}</Col>
        <Col md={6}><strong>Address:</strong> {patient?.trainingAddress || 'Training Scenario'}</Col>
      </Row>
    </Card.Body>
  </Card>
);

const PrescriptionRow = ({
  index, row, onChange, onRemove,
  library, routeOptions, frequencyOptions, datalistDoseUnitsId,
  disabled, removable,
}) => {
  const update = (k, v) => onChange(index, { ...row, [k]: v });
  const pickFromLibrary = (item) => {
    const { amount, unit } = defaultDoseFromItem(item);
    onChange(index, {
      ...row,
      drug: item.drugName,
      formulation: item.form || row.formulation,
      strength: formatLibraryStrength(item) || row.strength,
      route: item.defaultRoute || row.route,
      doseAmount: amount || row.doseAmount,
      doseUnit: unit || row.doseUnit,
      frequency: item.usualFrequencies || row.frequency,
      isControlledDrug: Boolean(item.controlledDrug),
    });
  };
  return (
    <div className="border rounded p-2 mb-2" style={{ backgroundColor: row.isControlledDrug ? '#fff8e1' : '#fafafa' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong className="small">Prescription {index + 1}{row.isControlledDrug ? ' (controlled drug)' : ''}</strong>
        {removable ? <Button size="sm" variant="outline-danger" onClick={() => onRemove(index)} disabled={disabled} title="Remove this prescription">×</Button> : null}
      </div>
      <Row className="g-2 align-items-end mb-2">
        <Col md={5}>
          <Form.Group>
            <Form.Label className="small mb-1">Drug</Form.Label>
            <DrugAutocomplete
              value={row.drug}
              onChange={(v) => update('drug', v)}
              onPick={pickFromLibrary}
              library={library}
              disabled={disabled}
            />
          </Form.Group>
        </Col>
        <Col md={4}><Form.Group><Form.Label className="small mb-1">Formulation</Form.Label><Form.Control size="sm" value={row.formulation} onChange={(e) => update('formulation', e.target.value)} disabled={disabled} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label className="small mb-1">Strength</Form.Label><Form.Control size="sm" value={row.strength} onChange={(e) => update('strength', e.target.value)} disabled={disabled} /></Form.Group></Col>
      </Row>
      <Row className="g-2 mb-2">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small mb-1">Route</Form.Label>
            <Form.Select size="sm" value={row.route} onChange={(e) => update('route', e.target.value)} disabled={disabled}>
              <option value="">Select route</option>
              {routeOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label className="small mb-1">Dose amount</Form.Label>
            <Form.Control size="sm" type="number" min="0" step="any" value={row.doseAmount} onChange={(e) => update('doseAmount', e.target.value)} disabled={disabled} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small mb-1">Dose unit</Form.Label>
            <Form.Control size="sm" list={datalistDoseUnitsId} value={row.doseUnit} onChange={(e) => update('doseUnit', e.target.value)} placeholder="tablet" disabled={disabled} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="small mb-1">Frequency</Form.Label>
            <Form.Select size="sm" value={row.frequency} onChange={(e) => update('frequency', e.target.value)} disabled={disabled}>
              <option value="">Select frequency</option>
              {frequencyOptions.map((f) => <option key={f} value={f}>{f}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-2 mb-2">
        <Col md={3}><Form.Group><Form.Label className="small mb-1">Duration (days)</Form.Label><Form.Control size="sm" type="number" min="0" value={row.durationDays} onChange={(e) => update('durationDays', e.target.value)} disabled={disabled} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label className="small mb-1">Duration (doses)</Form.Label><Form.Control size="sm" type="number" min="0" value={row.durationDoses} onChange={(e) => update('durationDoses', e.target.value)} disabled={disabled} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label className="small mb-1">Quantity (figures)</Form.Label><Form.Control size="sm" type="number" min="0" value={row.quantity} onChange={(e) => update('quantity', e.target.value)} disabled={disabled} /></Form.Group></Col>
      </Row>
      {row.isControlledDrug ? (
        <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#fff3cd' }}>
          <div className="small fw-semibold mb-2">Controlled drug requirements</div>
          <Row className="g-2 mb-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small mb-1">Total quantity in words (legal requirement)</Form.Label>
                <Form.Control size="sm" value={row.quantityInWords} onChange={(e) => update('quantityInWords', e.target.value)} placeholder="e.g. twenty-eight" disabled={disabled} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Instalment amount</Form.Label>
                <Form.Control size="sm" value={row.instalmentAmount} onChange={(e) => update('instalmentAmount', e.target.value)} placeholder="optional" disabled={disabled} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Instalment interval</Form.Label>
                <Form.Control size="sm" value={row.instalmentInterval} onChange={(e) => update('instalmentInterval', e.target.value)} placeholder="optional" disabled={disabled} />
              </Form.Group>
            </Col>
          </Row>
        </div>
      ) : null}
    </div>
  );
};

const FREQ_LABEL = (a) => {
  const parts = [];
  if (a.doseAmount != null && a.doseAmount !== '') parts.push(`${a.doseAmount} ${a.doseUnit || ''}`.trim());
  if (a.frequency) parts.push(a.frequency);
  return parts.join(' · ');
};

const PrescriptionDetail = ({ rx }) => {
  if (!rx) return null;
  const head = [rx.drug, rx.strength, rx.formulation].filter(Boolean).join(' ');
  const dose = FREQ_LABEL(rx);
  const route = rx.route || '';
  const duration = rx.durationDays
    ? `${rx.durationDays} day${rx.durationDays === 1 ? '' : 's'}`
    : (rx.durationDoses ? `${rx.durationDoses} dose${rx.durationDoses === 1 ? '' : 's'}` : '');
  const qty = rx.quantity != null && rx.quantity !== '' ? `Quantity: ${rx.quantity}` : '';
  return (
    <div className="small">
      <div className="fw-semibold">{head}</div>
      <div className="text-muted">
        {[dose, route, duration, qty].filter(Boolean).join(' | ')}
      </div>
    </div>
  );
};

const ResultPanel = ({ session, drill, customMustHaves, onPlayAgain, onBack }) => {
  if (!session) return null;
  const grading = session.grading || {};
  const ruleResults = Array.isArray(grading.ruleResults) ? grading.ruleResults : [];
  const evaluatedRules = ruleResults.filter((r) => r.passed === true || r.passed === false);
  const informationalRules = ruleResults.filter((r) => r.passed === null || r.passed === undefined);
  const slotResults = Array.isArray(grading.slotResults) ? grading.slotResults : [];
  const counsellingNotes = Array.isArray(grading.counsellingNotes) ? grading.counsellingNotes : [];
  const unmatched = Array.isArray(grading.unmatchedSubmissions) ? grading.unmatchedSubmissions : [];
  const studentCounselling = grading.studentCounselling || '';
  return (
    <div>
      <SimulationBanner />
      <div className="d-flex flex-wrap gap-3 mb-3">
        <Card className="flex-grow-1" style={{ minWidth: 180 }}>
          <Card.Body className="text-center py-3">
            <div className="text-muted small">Drug match score</div>
            <div className="display-6 fw-semibold">{Math.round(session.fieldScorePct ?? 0)}%</div>
            <div className="small text-muted">{grading.matchedSlotCount ?? 0} of {grading.expectedSlotCount ?? 0} matched</div>
          </Card.Body>
        </Card>
        <Card className="flex-grow-1" style={{ minWidth: 180 }}>
          <Card.Body className="text-center py-3">
            <div className="text-muted small">Legal / rule score</div>
            <div className="display-6 fw-semibold">{Math.round(session.ruleScorePct ?? 0)}%</div>
            <div className="small text-muted">{evaluatedRules.filter((r) => r.passed).length} of {evaluatedRules.length} rules met</div>
          </Card.Body>
        </Card>
        <Card className="flex-grow-1" style={{ minWidth: 180 }}>
          <Card.Body className="text-center py-3">
            <div className="text-muted small">Overall</div>
            <div className="display-6 fw-semibold">{Math.round(session.overallPct ?? 0)}%</div>
            {session.lecturerReviewRequired ? <Badge bg="info" className="mt-1">Lecturer review encouraged</Badge> : null}
          </Card.Body>
        </Card>
      </div>

      {slotResults.length > 0 ? (
        <div className="mb-3">
          <h6>Per-prescription breakdown</h6>
          {slotResults.map((slot, i) => (
            <Card key={slot.slotId || i} className={`mb-2 ${slot.matched ? 'border-success' : 'border-warning'}`}>
              <Card.Body className="py-2 px-3">
                <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                  <div>
                    <strong>{slot.slotLabel || `Prescription ${i + 1}`}</strong>
                    {slot.controlledDrug ? <Badge bg="warning" text="dark" className="ms-2">CD</Badge> : null}
                  </div>
                  {slot.matched ? <Badge bg="success">Matched</Badge> : <Badge bg="warning" text="dark">Missed</Badge>}
                </div>

                {slot.matched ? (
                  <Row className="g-2 mt-2">
                    <Col md={6}>
                      <div className="small text-muted mb-1">You prescribed</div>
                      <PrescriptionDetail rx={slot.submitted || { drug: slot.submittedDrug }} />
                    </Col>
                    <Col md={6}>
                      <div className="small text-muted mb-1">Expected (matched)</div>
                      <PrescriptionDetail rx={slot.matchedExpected || { drug: slot.matchedExpectedDrug }} />
                    </Col>
                    {slot.fields ? (
                      <Col xs={12}>
                        <div className="small mt-1">
                          {Object.entries(slot.fields).map(([k, v]) => (
                            <Badge key={k} bg={v ? 'success' : 'danger'} className="me-1 mb-1">
                              {k}: {v ? '✓' : '✗'}
                            </Badge>
                          ))}
                        </div>
                      </Col>
                    ) : null}
                    {Array.isArray(slot.bonusFields) && slot.bonusFields.length > 0 ? (
                      <Col xs={12}>
                        <div className="small text-muted mt-1">
                          <i className="bi bi-info-circle me-1" />
                          Bonus detail (not required, did not affect your score): {slot.bonusFields.join(', ')}
                        </div>
                      </Col>
                    ) : null}
                  </Row>
                ) : (
                  <div className="mt-2">
                    <div className="small text-muted mb-1">No matching drug submitted. Acceptable answers:</div>
                    {(slot.acceptableAnswers || []).map((a, ai) => (
                      <div key={ai} className="border rounded p-2 mb-1" style={{ backgroundColor: '#f8f9fa' }}>
                        <PrescriptionDetail rx={a} />
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      ) : null}

      {unmatched.length > 0 ? (
        <Alert variant="warning">
          <strong>You prescribed {unmatched.length} drug{unmatched.length === 1 ? '' : 's'} that the case did not call for:</strong> {unmatched.join(', ')}.
          {' '}This case expected {grading.expectedSlotCount ?? 0} prescription{(grading.expectedSlotCount ?? 0) === 1 ? '' : 's'}, you submitted {(grading.matchedSlotCount ?? 0) + unmatched.length}.
          Each unnecessary prescription reduces your drug score because over-prescribing carries real-world harm.
        </Alert>
      ) : null}

      {evaluatedRules.length > 0 ? (
        <div className="mb-3">
          <h6>Legal / rule checks</h6>
          {evaluatedRules.map((r, i) => (
            <div key={i} className="small mb-1">
              <Badge bg={r.passed ? 'success' : 'danger'} className="me-2">{r.passed ? '✓' : '✗'}</Badge>
              {r.key.replace(/_/g, ' ')}
              {r.notes ? <span className="text-muted ms-1">({r.notes})</span> : null}
            </div>
          ))}
        </div>
      ) : null}

      {studentCounselling ? (
        <div className="mb-3">
          <h6>Your counselling notes</h6>
          <Card>
            <Card.Body className="py-2 px-3 small" style={{ whiteSpace: 'pre-wrap' }}>
              {studentCounselling}
            </Card.Body>
          </Card>
          {counsellingNotes.length === 0 ? (
            <div className="small text-muted mt-2">
              <i className="bi bi-info-circle me-1" />
              The lecturer didn&apos;t specify required counselling for this case, so your notes weren&apos;t auto-graded - they&apos;ve been logged for the lecturer to review. Counselling on a take-home prescription is always good practice.
            </div>
          ) : null}
        </div>
      ) : null}

      {counsellingNotes.length > 0 || (Array.isArray(customMustHaves) && customMustHaves.length > 0) || informationalRules.length > 0 ? (
        <div className="mb-3">
          <h6>Lecturer counselling feedback</h6>
          {counsellingNotes.map((note, i) => (
            <Card key={`cf-${i}`} className="mb-2">
              <Card.Body className="py-2 px-3">
                <div className="small fw-semibold">{note.slotLabel || `Prescription`}{note.drug ? ` · ${note.drug}` : ''}</div>
                <div className="small">{note.feedback}</div>
              </Card.Body>
            </Card>
          ))}
          {Array.isArray(customMustHaves) && customMustHaves.length > 0 ? (
            <Card className="mb-2">
              <Card.Body className="py-2 px-3">
                <div className="small fw-semibold">From the lecturer</div>
                <ul className="mb-0 small">{customMustHaves.map((m, i) => <li key={i}>{m}</li>)}</ul>
              </Card.Body>
            </Card>
          ) : null}
          {informationalRules.map((r, i) => (
            <Card key={`ir-${i}`} className="mb-2">
              <Card.Body className="py-2 px-3">
                <div className="small">{r.key}</div>
              </Card.Body>
            </Card>
          ))}
        </div>
      ) : null}

      <div className="mt-4 d-flex gap-2 flex-wrap">
        <Button variant="primary" onClick={onPlayAgain}>Try again</Button>
        <Button variant="outline-secondary" onClick={onBack}>Back to library</Button>
      </div>
    </div>
  );
};

// ----- Verify-mode sub-components -----

// Renders the prescription as a printed document so verify mode feels
// closer to reading a real Rx. Cream paper background, serif body,
// patient block at the top, numbered items with "Sig:" / "Mitte:",
// signature + date strip at the bottom. Missing fields stay visible in
// red so they remain flaggable by the student.
const VerifyPrescriptionCard = ({ vp, patient }) => {
  if (!vp) return null;
  const drugs = Array.isArray(vp.drugs) ? vp.drugs : [];
  const paperStyle = {
    backgroundColor: '#fdfaf2',
    border: '1px solid #d0c8b8',
    fontFamily: '"Georgia", "Times New Roman", serif',
    color: '#1d1d1d',
  };
  const muteHeader = { letterSpacing: '0.1em', color: '#6c5d3b' };
  const sigLine = (d) => {
    const parts = [];
    if (d.doseAmount != null && d.doseAmount !== '') {
      parts.push(`${d.doseAmount}${d.doseUnit ? ` ${d.doseUnit}` : ''}`);
    }
    if (d.route) parts.push(d.route);
    if (d.frequency) parts.push(d.frequency);
    if (d.durationDays) parts.push(`for ${d.durationDays} day${d.durationDays === 1 ? '' : 's'}`);
    else if (d.durationDoses) parts.push(`for ${d.durationDoses} dose${d.durationDoses === 1 ? '' : 's'}`);
    return parts.join(' · ');
  };
  return (
    <>
      <p className="text-muted small mb-2">Read the prescription below carefully. It may be safe, may have legal or clinical issues, or both. Pick the right action at the bottom.</p>
      <Card className="mb-3" style={paperStyle}>
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-end pb-2 mb-3" style={{ borderBottom: '1px solid #c9c0a8' }}>
            <div>
              <div className="text-uppercase small" style={muteHeader}>Outpatient prescription</div>
              <div className="text-muted small fst-italic">Simulation only — not for clinical use</div>
            </div>
            <div className="text-end small">
              <div className="text-uppercase" style={muteHeader}>Date</div>
              <div className="fw-semibold">
                {vp.formMeta?.prescriptionDate || <em className="text-danger">missing</em>}
              </div>
            </div>
          </div>

          <Row className="g-3 small mb-3 pb-3" style={{ borderBottom: '1px dashed #c9c0a8' }}>
            <Col md={7}>
              <div className="text-uppercase mb-1" style={{ fontSize: '0.7rem', ...muteHeader }}>Patient</div>
              <div className="fw-semibold" style={{ fontSize: '1rem' }}>{patient?.name || '—'}</div>
              <div className="text-muted">{patient?.trainingAddress || 'Training Scenario'}</div>
            </Col>
            <Col md={2}>
              <div className="text-uppercase mb-1" style={{ fontSize: '0.7rem', ...muteHeader }}>DOB</div>
              <div>{patient?.dob || '—'}</div>
            </Col>
            <Col md={3}>
              <div className="text-uppercase mb-1" style={{ fontSize: '0.7rem', ...muteHeader }}>NHS no.</div>
              <div>{patient?.trainingNhsNumber || 'TRAINING-XXXXXXXXXX'}</div>
            </Col>
            <Col md={12}>
              <div className="text-uppercase mb-1" style={{ fontSize: '0.7rem', ...muteHeader }}>Allergies</div>
              <div>{patient?.allergies || 'NKDA'}</div>
            </Col>
          </Row>

          <ol className="ps-4 mb-4" style={{ listStyleType: 'decimal' }}>
            {drugs.map((d, i) => (
              <li key={i} className="mb-3">
                <div style={d.isControlledDrug ? {
                  backgroundColor: '#fff8e1',
                  padding: '0.5rem 0.75rem',
                  borderLeft: '3px solid #b78900',
                  marginLeft: '-0.5rem',
                } : null}>
                  <div className="fw-bold" style={{ fontSize: '1.05rem' }}>
                    {(d.drug || '—').toUpperCase()}{d.strength ? ` ${d.strength}` : ''}{d.formulation ? ` ${d.formulation}` : ''}
                    {d.isControlledDrug ? <Badge bg="warning" text="dark" className="ms-2">CD</Badge> : null}
                  </div>
                  <div>
                    <span className="text-uppercase me-2" style={{ fontSize: '0.75rem', ...muteHeader }}>Sig</span>
                    {sigLine(d) || <em className="text-danger">no instructions</em>}
                  </div>
                  <div>
                    <span className="text-uppercase me-2" style={{ fontSize: '0.75rem', ...muteHeader }}>Mitte</span>
                    {d.quantity != null && d.quantity !== '' ? d.quantity : <em className="text-danger">not stated</em>}
                    {d.isControlledDrug ? (
                      <>
                        {' '}
                        <span className="fst-italic">
                          ({d.quantityInWords
                            ? d.quantityInWords
                            : <em className="text-danger fst-normal">no words written</em>})
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <div className="pt-3" style={{ borderTop: '1px solid #c9c0a8' }}>
            <Row className="g-3 align-items-end">
              <Col md={7}>
                <div className="text-uppercase small mb-1" style={muteHeader}>Prescriber signature</div>
                <div style={{
                  borderBottom: '1px solid #6c5d3b',
                  minHeight: '2.2rem',
                  paddingBottom: '0.25rem',
                  fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
                  fontSize: '1.4rem',
                  color: '#274099',
                }}>
                  {vp.formMeta?.signaturePresent ? 'J. Smith' : ' '}
                </div>
                {!vp.formMeta?.signaturePresent ? (
                  <div className="small text-danger fst-italic mt-1">signature missing</div>
                ) : null}
              </Col>
              <Col md={5}>
                <div className="text-uppercase small mb-1" style={muteHeader}>Prescriber address</div>
                <div className="small">
                  {vp.formMeta?.prescriberAddress
                    ? vp.formMeta.prescriberAddress
                    : <em className="text-danger">missing</em>}
                </div>
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

const VerifyFlagPicker = ({ catalogue = [], drugs = [], flags = [], onAdd, onRemove, onUpdateDrug, disabled }) => {
  const [picker, setPicker] = useState('');
  const drugOptions = drugs.map((d) => d.drug).filter(Boolean);
  const handleAdd = () => {
    if (!picker) return;
    onAdd({ key: picker, drug: '' });
    setPicker('');
  };
  return (
    <Card className="mb-3">
      <Card.Body>
        <h5 className="mb-2">Errors you&apos;ve spotted</h5>
        <p className="text-muted small mb-3">Tick every error you can identify. Over-flagging costs precision; missing errors costs recall. If the prescription is safe, leave the list empty and pick <strong>Sign off as safe</strong>.</p>
        <Row className="g-2 align-items-end mb-3">
          <Col md={9}>
            <Form.Select size="sm" value={picker} onChange={(e) => setPicker(e.target.value)} disabled={disabled}>
              <option value="">Pick an error from the catalogue…</option>
              <optgroup label="Clinical (would harm the patient)">
                {catalogue.filter((c) => c.severity === 'clinical').map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </optgroup>
              <optgroup label="Legal (would not be dispensed)">
                {catalogue.filter((c) => c.severity === 'legal').map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </optgroup>
              <optgroup label="Best practice (safe but suboptimal)">
                {catalogue.filter((c) => c.severity === 'best_practice').map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </optgroup>
            </Form.Select>
          </Col>
          <Col md={3}>
            <Button size="sm" variant="primary" onClick={handleAdd} disabled={!picker || disabled}>+ Add flag</Button>
          </Col>
        </Row>
        {flags.length === 0 ? (
          <Alert variant="light" className="text-muted small mb-0 border">No flags raised yet.</Alert>
        ) : flags.map((f, i) => {
          const cat = catalogue.find((c) => c.key === f.key);
          if (!cat) return null;
          return (
            <div key={i} className="border rounded p-2 mb-2" style={{ backgroundColor: '#fafafa' }}>
              <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                <div>
                  <Badge {...SEVERITY_BADGE(cat.severity)} className="me-2">{SEVERITY_LABEL(cat.severity)}</Badge>
                  <span className="fw-semibold">{cat.label}</span>
                </div>
                <Button size="sm" variant="outline-danger" onClick={() => onRemove(i)} disabled={disabled} title="Remove this flag">×</Button>
              </div>
              {cat.appliesTo === 'per_drug' ? (
                <Form.Group>
                  <Form.Label className="small mb-1">Applies to</Form.Label>
                  <Form.Select size="sm" value={f.drug || ''} onChange={(e) => onUpdateDrug(i, e.target.value)} disabled={disabled}>
                    <option value="">Pick the drug</option>
                    {drugOptions.map((dn) => <option key={dn} value={dn}>{dn}</option>)}
                  </Form.Select>
                </Form.Group>
              ) : (
                <div className="small text-muted">Applies to the whole prescription</div>
              )}
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
};

const VerifyActionPicker = ({
  actions = [],
  value,
  onChange,
  rationale,
  onRationaleChange,
  disabled,
}) => {
  const requiresRationale = VERIFY_ACTIONS_REQUIRE_RATIONALE.has(value);
  const prompt = RATIONALE_PROMPTS[value] || 'Write a short explanation of your decision.';
  return (
    <Card className="mb-3">
      <Card.Body>
        <h5 className="mb-2">Your action</h5>
        <p className="text-muted small mb-3">Based on what you found, what would you do with this prescription?</p>
        <div className="d-flex gap-2 flex-wrap">
          {actions.map((a) => {
            const meta = VERIFY_ACTION_LABELS[a] || { label: a, icon: 'bi-question-circle', variant: 'secondary' };
            const selected = value === a;
            return (
              <Button
                key={a}
                variant={selected ? meta.variant : `outline-${meta.variant}`}
                onClick={() => onChange(a)}
                disabled={disabled}
                type="button"
              >
                <i className={`bi ${meta.icon} me-2`} />
                {meta.label}
              </Button>
            );
          })}
        </div>
        {requiresRationale ? (
          <Form.Group className="mt-3">
            <Form.Label className="fw-semibold mb-1">
              {prompt} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rationale || ''}
              onChange={(e) => onRationaleChange(e.target.value)}
              disabled={disabled}
              placeholder="Document your reasoning as you would in clinical practice…"
            />
          </Form.Group>
        ) : null}
      </Card.Body>
    </Card>
  );
};

const VerifyResultPanel = ({ session, onPlayAgain, onBack }) => {
  if (!session) return null;
  const grading = session.grading || {};
  const flagResults = Array.isArray(grading.flagResults) ? grading.flagResults : [];
  const truePositives = flagResults.filter((r) => r.status === 'true_positive');
  const falsePositives = flagResults.filter((r) => r.status === 'false_positive' || r.status === 'invalid');
  const missed = Array.isArray(grading.missedErrors) ? grading.missedErrors : [];
  const correctActionMeta = VERIFY_ACTION_LABELS[grading.correctAction] || {};
  const submittedActionMeta = VERIFY_ACTION_LABELS[grading.submittedAction] || {};
  return (
    <div>
      <SimulationBanner />
      <div className="d-flex flex-wrap gap-3 mb-3">
        <Card className="flex-grow-1" style={{ minWidth: 180 }}>
          <Card.Body className="text-center py-3">
            <div className="text-muted small">Flag F1</div>
            <div className="display-6 fw-semibold">{Math.round(grading.f1 ?? 0)}%</div>
            <div className="small text-muted">precision {Math.round(grading.precision ?? 0)}% · recall {Math.round(grading.recall ?? 0)}%</div>
          </Card.Body>
        </Card>
        <Card className="flex-grow-1" style={{ minWidth: 180 }}>
          <Card.Body className="text-center py-3">
            <div className="text-muted small">Action</div>
            <div className="display-6 fw-semibold">{grading.actionCorrect ? '✓' : '✗'}</div>
            <div className="small text-muted">
              You picked <strong>{verifyActionLabel(grading.submittedAction)}</strong>
              {grading.actionCorrect ? null : <><br />Correct: <strong>{verifyActionLabel(grading.correctAction)}</strong></>}
            </div>
          </Card.Body>
        </Card>
        <Card className="flex-grow-1" style={{ minWidth: 180 }}>
          <Card.Body className="text-center py-3">
            <div className="text-muted small">Overall</div>
            <div className="display-6 fw-semibold">{Math.round(session.overallPct ?? 0)}%</div>
            {session.lecturerReviewRequired ? <Badge bg="info" className="mt-1">Lecturer review encouraged</Badge> : null}
          </Card.Body>
        </Card>
      </div>

      {truePositives.length > 0 ? (
        <div className="mb-3">
          <h6>Errors you correctly flagged</h6>
          {truePositives.map((r, i) => (
            <Card key={i} className="mb-2 border-success">
              <Card.Body className="py-2 px-3">
                <div className="small">
                  <Badge {...SEVERITY_BADGE(r.severity)} className="me-2">{SEVERITY_LABEL(r.severity)}</Badge>
                  <span className="fw-semibold">{(grading.flagResults && grading.missedErrors)
                    ? r.key.replace(/_/g, ' ') : r.key}</span>
                  {r.drug ? <span className="text-muted"> · {r.drug}</span> : null}
                </div>
                {r.note ? (
                  <div className="small text-muted mt-1"><strong>Lecturer note:</strong> {r.note}</div>
                ) : null}
              </Card.Body>
            </Card>
          ))}
        </div>
      ) : null}

      {missed.length > 0 ? (
        <div className="mb-3">
          <h6>Errors you missed</h6>
          {missed.map((m, i) => (
            <Card key={i} className="mb-2 border-warning">
              <Card.Body className="py-2 px-3">
                <div className="small">
                  <Badge {...SEVERITY_BADGE(m.severity)} className="me-2">{SEVERITY_LABEL(m.severity)}</Badge>
                  <span className="fw-semibold">{m.label || m.key}</span>
                  {m.drug ? <span className="text-muted"> · {m.drug}</span> : null}
                </div>
                {m.note ? (
                  <div className="small text-muted mt-1"><strong>Why this matters:</strong> {m.note}</div>
                ) : null}
              </Card.Body>
            </Card>
          ))}
        </div>
      ) : null}

      {falsePositives.length > 0 ? (
        <div className="mb-3">
          <h6>Flags you raised that the lecturer didn&apos;t expect</h6>
          {falsePositives.map((r, i) => (
            <Alert key={i} variant="warning" className="py-2 px-3 small mb-2">
              <strong>{r.key.replace(/_/g, ' ')}</strong>{r.drug ? ` · ${r.drug}` : ''}
              {' '}— over-flagging reduces your precision score. The lecturer didn&apos;t mark this as an error in this prescription.
            </Alert>
          ))}
        </div>
      ) : null}

      {grading.authoredCount === 0 && truePositives.length === 0 && falsePositives.length === 0 ? (
        <Alert variant={grading.actionCorrect ? 'success' : 'warning'}>
          This prescription contained no errors. {grading.actionCorrect
            ? 'You correctly chose to sign it off as safe.'
            : `You should have signed off as safe (you picked "${verifyActionLabel(grading.submittedAction)}").`}
        </Alert>
      ) : null}

      {grading.actionRationale ? (
        <Card className="mb-3">
          <Card.Body className="py-2 px-3">
            <div className="text-muted small text-uppercase mb-1">Your rationale</div>
            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{grading.actionRationale}</div>
          </Card.Body>
        </Card>
      ) : null}

      <div className="mt-4 d-flex gap-2 flex-wrap">
        <Button variant="primary" onClick={onPlayAgain}>Try again</Button>
        <Button variant="outline-secondary" onClick={onBack}>Back to library</Button>
      </div>
    </div>
  );
};

const StudentOutpatientWorkspace = ({
  token,
  user,
  tenant,
  onBack,
  onUpgrade,
  previewMode = false,
  previewPayload = null,
  previewTitle = '',
  onExitPreview,
}) => {
  const isPaywallTenant = tenant?.tenantType === 'central_students';
  const subscriptionStatus = user?.subscriptionStatus || 'free';
  const isSubscribed = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
  const paywallActive = isPaywallTenant && !isSubscribed;
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSession, setActiveSession] = useState(null);
  const [activeDrill, setActiveDrill] = useState(null);
  const [drugs, setDrugs] = useState([blankDrugRow()]);
  const [formMeta, setFormMeta] = useState({
    prescriberAddress: SIMULATION_PRESCRIBER_ADDRESS,
  });
  const [counselling, setCounselling] = useState('');
  // Verify-mode state. flags is array of { key, drug? }; action is one of
  // the keys from snapshot.verifyActions; actionRationale is free text the
  // student must write to justify any non-sign-off action.
  const [verifyFlags, setVerifyFlags] = useState([]);
  const [verifyAction, setVerifyAction] = useState('');
  const [verifyActionRationale, setVerifyActionRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultSession, setResultSession] = useState(null);
  const [drugLibrary, setDrugLibrary] = useState([]);
  const [pendingWarnings, setPendingWarnings] = useState([]);
  const [interactionChecking, setInteractionChecking] = useState(false);
  const [routeOptions, setRouteOptions] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const datalistDoseUnitsRef = useRef('outprx-student-dose-units');
  const datalistDoseUnitsId = datalistDoseUnitsRef.current;

  const refresh = useCallback(async () => {
    if (previewMode) { setLoading(false); return; }
    try {
      setLoading(true);
      const result = await api.listOutpatientDrills(token);
      setDrills(result.drills || []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load case studies.');
    } finally {
      setLoading(false);
    }
  }, [token, previewMode]);

  useEffect(() => { refresh(); }, [refresh]);

  const startPreview = useCallback(async () => {
    if (!previewPayload) return;
    try {
      const result = await api.previewOutpatientDrillStart(previewPayload, token);
      setActiveSession({
        id: 'preview',
        drillId: 'preview',
        drillTitle: previewTitle || 'Preview',
        attemptNumber: 1,
        status: 'in_progress',
        snapshot: result.preview?.snapshot || {},
      });
      setActiveDrill({ id: 'preview', title: previewTitle || 'Preview', payload: previewPayload });
      setDrugs([blankDrugRow()]);
      setFormMeta({ prescriberAddress: SIMULATION_PRESCRIBER_ADDRESS });
      setCounselling('');
      setVerifyFlags([]);
      setVerifyAction('');
      setVerifyActionRationale('');
      setResultSession(null);
      setError('');
    } catch (err) {
      const msg = err?.payload?.error || err?.message || 'Could not start preview.';
      setError(msg);
    }
  }, [previewPayload, previewTitle, token]);

  useEffect(() => {
    if (previewMode && previewPayload && !activeSession && !resultSession) {
      startPreview();
    }
  }, [previewMode, previewPayload, activeSession, resultSession, startPreview]);

  useEffect(() => {
    let cancelled = false;
    api.getDrugLibrary()
      .then((res) => {
        if (cancelled) return;
        setDrugLibrary(res?.items || []);
        setRouteOptions(Array.isArray(res?.metadata?.routes) ? res.metadata.routes : []);
        setFrequencyOptions(Array.isArray(res?.metadata?.frequencies) ? res.metadata.frequencies : []);
      })
      .catch(() => { /* show empty dropdowns */ });
    return () => { cancelled = true; };
  }, []);

  const startCase = useCallback(async (drill) => {
    try {
      const result = await api.startOutpatientDrill(drill.id, token);
      setActiveSession(result.session);
      setActiveDrill(drill);
      setDrugs([blankDrugRow()]);
      setFormMeta({ prescriberAddress: SIMULATION_PRESCRIBER_ADDRESS });
      setCounselling('');
      setVerifyFlags([]);
      setVerifyAction('');
      setVerifyActionRationale('');
      setResultSession(null);
      setError('');
    } catch (err) {
      const msg = err?.payload?.error || err?.message || 'Could not start case study.';
      setError(msg);
    }
  }, [token]);

  const updateDrug = (idx, row) => setDrugs((curr) => curr.map((r, i) => (i === idx ? row : r)));
  const addDrug = () => setDrugs((curr) => [...curr, blankDrugRow()]);
  const removeDrug = (idx) => setDrugs((curr) => curr.length > 1 ? curr.filter((_, i) => i !== idx) : curr);

  const submit = useCallback(async () => {
    if (!activeSession || submitting) return;
    const mode = activeSession.snapshot?.mode || 'prescribe';
    setSubmitting(true);
    setPendingWarnings([]);
    try {
      let submission;
      if (mode === 'verify') {
        submission = {
          flags: verifyFlags
            .filter((f) => f.key)
            .map((f) => ({ key: f.key, drug: (f.drug || '').trim() })),
          action: verifyAction,
          actionRationale: verifyActionRationale.trim(),
        };
      } else {
        submission = {
          drugs: drugs
            .filter((d) => d.drug.trim())
            .map((d) => ({
              drug: d.drug.trim(),
              formulation: d.formulation.trim(),
              strength: d.strength.trim(),
              route: d.route.trim(),
              doseAmount: d.doseAmount === '' ? null : Number(d.doseAmount),
              doseUnit: d.doseUnit.trim(),
              frequency: d.frequency.trim(),
              durationDays: d.durationDays === '' ? null : Number(d.durationDays),
              durationDoses: d.durationDoses === '' ? null : Number(d.durationDoses),
              quantity: d.quantity === '' ? null : Number(d.quantity),
              quantityInWords: d.quantityInWords.trim(),
              instalmentAmount: d.instalmentAmount.trim(),
              instalmentInterval: d.instalmentInterval.trim(),
            })),
          formMeta: {
            prescriberAddress: formMeta.prescriberAddress.trim(),
          },
          counselling: counselling.trim(),
        };
      }
      if (previewMode) {
        const result = await api.previewOutpatientDrillGrade(previewPayload, submission, token);
        setResultSession({ id: 'preview', status: 'completed', ...(result.preview || {}) });
      } else {
        const result = await api.submitOutpatientSession(activeSession.id, submission, token);
        setResultSession(result.session);
      }
      setActiveSession(null);
    } catch (err) {
      const msg = err?.payload?.error || err?.message || 'Could not submit.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [activeSession, drugs, formMeta, counselling, verifyFlags, verifyAction, verifyActionRationale, token, submitting, previewMode, previewPayload]);

  const onSubmitClick = useCallback(async () => {
    if (!activeSession || submitting || interactionChecking) return;
    const mode = activeSession.snapshot?.mode || 'prescribe';
    if (mode !== 'prescribe') {
      submit();
      return;
    }
    const entries = drugs
      .filter((d) => d.drug.trim())
      .map((d, idx) => ({ id: `row-${idx}`, name: d.drug.trim() }));
    if (entries.length === 0) {
      submit();
      return;
    }
    setInteractionChecking(true);
    try {
      const warnings = await runFullInteractionCheck({ entries, token, drugLibrary });
      if (warnings.length === 0) {
        submit();
        return;
      }
      setPendingWarnings(warnings);
    } finally {
      setInteractionChecking(false);
    }
  }, [activeSession, submitting, interactionChecking, drugs, token, drugLibrary, submit]);

  const acknowledgeWarningsAndSubmit = useCallback(() => {
    setPendingWarnings([]);
    submit();
  }, [submit]);

  // Active session - the simulated prescription form
  if (activeSession) {
    const snap = activeSession.snapshot || {};
    const isVerify = snap.mode === 'verify';
    const hasAnyDrugFilled = drugs.some((d) => d.drug.trim());
    // Verify-mode submit gate: action must be picked. Flags can be empty
    // (zero-error case is valid - student should pick "No errors found").
    // Per-drug flags must each have a target drug. Actions other than
    // sign_off must come with a non-empty rationale (educational reason
    // for forcing the student to articulate their reasoning).
    const rationaleRequired = VERIFY_ACTIONS_REQUIRE_RATIONALE.has(verifyAction);
    const rationaleOk = !rationaleRequired || Boolean(verifyActionRationale.trim());
    const verifyReady = isVerify
      && Boolean(verifyAction)
      && rationaleOk
      && verifyFlags.every((f) => {
        const cat = (snap.errorCatalogue || []).find((c) => c.key === f.key);
        if (!cat) return false;
        if (cat.appliesTo === 'per_drug') return Boolean((f.drug || '').trim());
        return true;
      });
    return (
      <div className="student-page">
        <Container className="mt-4 mb-5 student-page__content">
          <datalist id={datalistDoseUnitsId}>
            {COMMON_DOSE_UNITS.map((u) => <option key={u} value={u} />)}
          </datalist>
          <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
            <h2 className="mb-0">
              {activeDrill?.title || 'Outpatient case study'}
            </h2>
            {previewMode || !isVerify ? (
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setActiveSession(null); setActiveDrill(null);
                  if (previewMode && onExitPreview) onExitPreview();
                }}
                disabled={submitting}
              >
                {previewMode ? 'Exit preview' : 'Abandon'}
              </Button>
            ) : null}
          </div>
          {error ? <Alert variant="danger">{error}</Alert> : null}
          {previewMode ? (
            <Alert variant="info" className="mb-3 text-center fw-semibold">
              PREVIEW MODE - This is exactly what students will see. No session is saved.
            </Alert>
          ) : null}

          <SimulationBanner />

          <ScenarioBanner scenario={snap.scenario} />

          {isVerify ? null : <PatientHeader patient={snap.patient} />}

          {isVerify ? (
            <>
              <VerifyPrescriptionCard vp={snap.verifyPrescription} patient={snap.patient} />
              <VerifyFlagPicker
                catalogue={snap.errorCatalogue || []}
                drugs={snap.verifyPrescription?.drugs || []}
                flags={verifyFlags}
                onAdd={(f) => setVerifyFlags((curr) => [...curr, f])}
                onRemove={(idx) => setVerifyFlags((curr) => curr.filter((_, i) => i !== idx))}
                onUpdateDrug={(idx, drug) => setVerifyFlags((curr) => curr.map((f, i) => i === idx ? { ...f, drug } : f))}
                disabled={submitting}
              />
              <VerifyActionPicker
                actions={snap.verifyActions || ['sign_off', 'refer_back', 'refuse_to_dispense']}
                value={verifyAction}
                onChange={setVerifyAction}
                rationale={verifyActionRationale}
                onRationaleChange={setVerifyActionRationale}
                disabled={submitting}
              />

              <SimulationFooter />

              <div className="mt-3 d-flex gap-2 flex-wrap">
                <Button variant="primary" onClick={submit} disabled={submitting || !verifyReady}>
                  {submitting ? <><Spinner animation="border" size="sm" /> Submitting…</> : 'Submit verification'}
                </Button>
                <Button variant="outline-secondary" onClick={() => { setActiveSession(null); setActiveDrill(null); }} disabled={submitting}>Cancel</Button>
              </div>
            </>
          ) : (
            <>
              <Card className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Your prescription</h5>
                    <Button size="sm" variant="outline-primary" onClick={addDrug} disabled={submitting}>+ Add another drug</Button>
                  </div>
                  <p className="text-muted small mb-3">
                    Add as many or as few drugs as you think the case requires. Pick each drug from the library; route, dose unit and frequency match the lecturer&apos;s answer key when you select the same options.
                  </p>
                  {drugs.map((row, idx) => (
                    <PrescriptionRow
                      key={idx}
                      index={idx}
                      row={row}
                      onChange={updateDrug}
                      onRemove={removeDrug}
                      library={drugLibrary}
                      routeOptions={routeOptions}
                      frequencyOptions={frequencyOptions}
                      datalistDoseUnitsId={datalistDoseUnitsId}
                      disabled={submitting}
                      removable={drugs.length > 1}
                    />
                  ))}
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Body>
                  <h5 className="mb-2">Counselling notes</h5>
                  <p className="text-muted small mb-2">Write what you would say to the patient when handing over the prescription.</p>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={counselling}
                    onChange={(e) => setCounselling(e.target.value)}
                    placeholder="e.g. Take with food. Complete the full course even if you feel better. Drink plenty of fluids…"
                    disabled={submitting}
                  />
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Body>
                  <h5 className="mb-2">Prescriber details</h5>
                  <Form.Group>
                    <Form.Label className="small mb-1">Prescriber address (auto, simulation only)</Form.Label>
                    <Form.Control size="sm" value={formMeta.prescriberAddress} disabled />
                  </Form.Group>
                </Card.Body>
              </Card>

              <SimulationFooter />

              <div className="mt-3 d-flex gap-2 flex-wrap">
                <Button variant="primary" onClick={onSubmitClick} disabled={submitting || interactionChecking || !hasAnyDrugFilled}>
                  {submitting
                    ? <><Spinner animation="border" size="sm" /> Submitting…</>
                    : interactionChecking
                      ? <><Spinner animation="border" size="sm" /> Checking…</>
                      : 'Submit prescription'}
                </Button>
                <Button variant="outline-secondary" onClick={() => { setActiveSession(null); setActiveDrill(null); }} disabled={submitting}>Cancel</Button>
              </div>
            </>
          )}
          <InteractionWarningModal
            warnings={pendingWarnings}
            acknowledgeLabel="Submit anyway"
            onCancel={() => setPendingWarnings([])}
            onAcknowledge={acknowledgeWarningsAndSubmit}
          />
        </Container>
      </div>
    );
  }

  // Result view
  if (resultSession) {
    const customMustHaves = activeDrill?.payload?.customMustHaves || [];
    const isVerifyResult = resultSession.grading?.mode === 'verify';
    const handleAgain = () => {
      if (previewMode) {
        setResultSession(null);
        startPreview();
      } else if (activeDrill) {
        startCase(activeDrill);
      }
    };
    const handleBack = () => {
      setResultSession(null); setActiveDrill(null);
      if (previewMode && onExitPreview) onExitPreview();
    };
    return (
      <div className="student-page">
        <Container className="mt-4 mb-5 student-page__content">
          <h2 className="mb-3">
            Result · {activeDrill?.title || 'Case study complete'}
            {isVerifyResult ? <Badge bg="info" className="ms-2"><i className="bi bi-search me-1" />Verify</Badge> : null}
          </h2>
          {previewMode ? (
            <Alert variant="info" className="mb-3 text-center fw-semibold">
              PREVIEW MODE - This is exactly what students will see after submitting. No session is saved.
            </Alert>
          ) : null}
          {isVerifyResult ? (
            <VerifyResultPanel session={resultSession} onPlayAgain={handleAgain} onBack={handleBack} />
          ) : (
            <ResultPanel
              session={resultSession}
              drill={activeDrill}
              customMustHaves={customMustHaves}
              onPlayAgain={handleAgain}
              onBack={handleBack}
            />
          )}
        </Container>
      </div>
    );
  }

  // Preview-mode fallback while preview-start request is in flight
  if (previewMode) {
    return (
      <div className="student-page">
        <Container className="mt-4 mb-5 student-page__content">
          {error ? <Alert variant="danger">{error}</Alert> : <div className="text-center mt-4"><Spinner animation="border" /></div>}
          {onExitPreview ? (
            <div className="text-center mt-3">
              <Button variant="outline-secondary" onClick={onExitPreview}>Back to editor</Button>
            </div>
          ) : null}
        </Container>
      </div>
    );
  }

  // Catalog
  return (
    <div className="student-page">
      <Container className="mt-4 mb-5 student-page__content">
        <div className="student-dashboard-header shadow-sm rounded mb-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h2 className="mb-2">Outpatient / clinic case studies</h2>
              <p className="student-dashboard-header__copy mb-0">
                Clinic-style scenarios. Read the case, then write a take-home prescription on the simulated outpatient form.
                Pick the drug, route, dose and frequency from the library dropdowns - the same options the lecturer used in the answer key.
                Controlled-drug legal requirements apply automatically when you pick a CD.
              </p>
              {onBack ? (
                <Button type="button" variant="outline-primary" className="btn-sm mt-2" onClick={onBack}>
                  <i className="bi bi-arrow-left" aria-hidden="true" />{' '}
                  Back
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        {paywallActive ? (
          <Alert variant="warning" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <i className="bi bi-lock-fill me-2" />
              <strong>You&apos;re on the free tier.</strong> Locked case studies need a subscription to start.
            </div>
            {onUpgrade ? <Button size="sm" variant="warning" onClick={onUpgrade}>Subscribe to unlock</Button> : null}
          </Alert>
        ) : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {loading ? (
          <div className="text-center mt-4"><Spinner animation="border" /></div>
        ) : drills.length === 0 ? (
          <Alert variant="info" className="mt-4">
            No outpatient case studies are published yet. Check back later or ask your lecturer to publish one.
          </Alert>
        ) : (
          <Card>
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Case study</th>
                  <th style={{ width: 110 }}>Mode</th>
                  <th style={{ width: 120 }}>Difficulty</th>
                  {isPaywallTenant ? <th style={{ width: 120 }}>Tier</th> : null}
                  <th style={{ width: 160 }}>Last attempted</th>
                  <th style={{ width: 200 }} className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {drills.map((drill) => {
                  const diff = labelForDifficulty(drill.difficulty);
                  const locked = paywallActive && !drill.freeTier;
                  const isVerify = drill.mode === 'verify';
                  return (
                    <tr key={drill.id} style={locked ? { opacity: 0.55 } : undefined}>
                      <td>
                        <div className="fw-semibold">
                          {locked ? <i className="bi bi-lock-fill me-2 text-warning" title="Locked - subscribe to unlock" /> : null}
                          {drill.title}
                        </div>
                        {drill.summary ? <div className="small text-muted">{drill.summary}</div> : null}
                      </td>
                      <td>
                        {isVerify
                          ? <Badge bg="info"><i className="bi bi-search me-1" />Verify</Badge>
                          : <Badge bg="primary"><i className="bi bi-pencil-square me-1" />Prescribe</Badge>}
                      </td>
                      <td><Badge bg={diff.tone}>{diff.label}</Badge></td>
                      {isPaywallTenant ? (
                        <td>
                          {drill.freeTier
                            ? <Badge bg="success">Free</Badge>
                            : <Badge bg="secondary">Premium</Badge>}
                        </td>
                      ) : null}
                      <td className="small text-muted">{formatRelativeDate(drill.lastAttemptedAt)}</td>
                      <td className="text-end">
                        {locked ? (
                          onUpgrade
                            ? <Button size="sm" variant="outline-dark" onClick={onUpgrade}><i className="bi bi-lock-fill me-1" /> Subscribe to unlock</Button>
                            : <Button size="sm" variant="outline-secondary" disabled><i className="bi bi-lock-fill me-1" /> Locked</Button>
                        ) : (
                          <Button size="sm" variant="primary" onClick={() => startCase(drill)}>
                            {drill.lastAttemptedAt ? 'Re-attempt' : 'Start case study'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default StudentOutpatientWorkspace;
